import { useEffect, useState } from 'react';
import { Button as RACButton } from 'react-aria-components';

import { useBridgewayApi } from '@/api/BridgewayApi';
import { useUserId } from '@/auth/UserIdContext';
import { logger } from '@/logger';
import { SeededJobPanel } from '@/spaces/chat/panels/SeededJobPanel';
import {
	jobFindTitle,
	useJobInspirationChat,
} from '@/spaces/chat/useJobInspirationChat';
import { cn } from '@/utils/cn';
import { formatRelativeTime } from '@/utils/formatRelativeTime';

import type {
	Artifact,
	InspirationReference,
} from '../../../../../bridge/types/artifact.ts';
import { isInspirationReference } from '../../../../../bridge/types/artifact.ts';
import type { PanelProcess } from '../../../../../bridge/types/state.ts';

// How many finds the panel lists. NOT the route's maximum, which is 50
// (LEARNING_PROJECTION_MAX in services/py/api/routes/artifacts.py): each row here is
// a WHOLE artifact document, so asking for the server's limit would transfer far
// more than the panel shows.
const FIND_LIMIT = 20;

type LoadState = 'loading' | 'loaded' | 'failed';

const SectionHeading = ({ children }: { children: React.ReactNode }) => (
	<h3
		className="mb-3 text-[11px] font-semibold tracking-[0.12em]
			text-(--text-muted) uppercase"
	>
		{children}
	</h3>
);

/** The row's body sentence, and what stands in when a find has none.
 *
 * A find saved before the analysis carried a `learning` has an empty one, so the
 * fallback is what keeps an existing customer's panel from rendering a column of
 * blank rows. Returns an empty string when the find carries no prose at all, and
 * the caller then draws no body element rather than an empty box.
 *
 * `topic` is NOT in this chain, because the row title already renders it. Adding
 * it here printed the same words twice in one row for exactly the find this
 * fallback exists to serve, and it hid `suggestion`, which tells the customer
 * what to borrow.
 */
const rowBody = (content: InspirationReference['content']): string =>
	content.learning?.trim() || content.suggestion?.trim() || '';

const FindRow = ({ find }: { find: InspirationReference }) => {
	// One artifact per row, so the hook lives here rather than in the list loop.
	// This is the same press target the home feed uses, so a find opens the same
	// conversation from either surface.
	const onPress = useJobInspirationChat(find);
	const title = jobFindTitle(find);
	const body = rowBody(find.content);

	return (
		<RACButton
			onPress={onPress}
			aria-label={`Open the conversation about ${title}`}
			className={cn(
				'flex cursor-pointer flex-col gap-1 rounded-lg border',
				'border-(--glass-border) px-3 py-2 text-left outline-none',
				'hover:bg-black/[0.02]',
			)}
		>
			<div className="flex items-center gap-2 text-xs text-(--text-muted)">
				<span>{formatRelativeTime(find.content.shared_at)}</span>
			</div>
			<span className="text-sm break-words text-(--text-primary)">{title}</span>
			{body && (
				<p className="line-clamp-2 text-xs text-(--text-secondary)">{body}</p>
			)}
		</RACButton>
	);
};

interface SimilarBusinessInsightsPanelProps {
	panelProcess: PanelProcess;
	onBack?: () => void;
	onClose: () => void;
	style?: React.CSSProperties;
}

export const SimilarBusinessInsightsPanel = ({
	panelProcess,
	onBack,
	onClose,
	style,
}: SimilarBusinessInsightsPanelProps) => {
	const userId = useUserId();
	const api = useBridgewayApi();
	const [finds, setFinds] = useState<InspirationReference[]>([]);
	const [state, setState] = useState<LoadState>('loading');

	useEffect(() => {
		let isCurrent = true;
		if (!userId) {
			setState('loaded');
			return () => {
				isCurrent = false;
			};
		}
		setState('loading');
		api
			.get<Artifact[]>(
				`/api/user/${userId}/artifacts/search/job-inspirations?limit=${FIND_LIMIT}`,
			)
			.then((rows) => {
				if (!isCurrent) return;
				const list = Array.isArray(rows) ? rows : [];
				setFinds(
					list
						.filter(isInspirationReference)
						// The same guard the home feed keeps: a link the customer pasted
						// is the same artifact type, and this job must never claim one as
						// something it found.
						.filter((a) => Boolean(a.content.found_by_job_for?.trim())),
				);
				setState('loaded');
			})
			.catch((error) => {
				if (!isCurrent) return;
				logger.error('SimilarBusinessInsightsPanel finds fetch failed', error);
				// A failed read must never render as "the job found nothing".
				setState('failed');
			});
		return () => {
			isCurrent = false;
		};
	}, [api, userId]);

	return (
		<SeededJobPanel
			panelProcess={panelProcess}
			// Reached only before the stored process row loads. Matches
			// COMPETITOR_VIDEO_INSIGHTS_JOB_NAME in
			// services/py/shared/agents/competitor_video_insights_agent.py.
			fallbackTitle="Insights from similar businesses"
			isLoading={false}
			// The body opens by explaining the job, so the seeded summary would
			// restate the name directly under it.
			showDescription={false}
			onBack={onBack}
			onClose={onClose}
			style={style}
		>
			<div className="mt-5 flex flex-col gap-6">
				<section>
					<SectionHeading>How it works</SectionHeading>
					<div className="text-sm text-(--text-primary)">
						Every day, Bridgeway studies businesses like yours on Instagram. It
						reads the posts that got the most engagement. It saves each post it
						can learn from, and it writes down the lesson. Open a find to talk
						about it.
					</div>
				</section>

				<section data-testid="similar-business-finds">
					<SectionHeading>What we have learned</SectionHeading>
					{state === 'loading' && (
						<p className="text-sm text-(--text-muted)">Loading recent finds…</p>
					)}
					{state === 'failed' && (
						<p
							data-testid="similar-business-finds-error"
							className="text-sm text-(--text-muted)"
						>
							Could not load the recent finds. Try again in a moment.
						</p>
					)}
					{state === 'loaded' && finds.length === 0 && (
						<p className="text-sm text-(--text-muted)">
							Nothing yet. This job adds what it learns after each daily study.
						</p>
					)}
					{state === 'loaded' && finds.length > 0 && (
						<div className="flex flex-col gap-2">
							{finds.map((find) => (
								<FindRow key={find.id} find={find} />
							))}
						</div>
					)}
				</section>
			</div>
		</SeededJobPanel>
	);
};
